import { useEffect, useState } from "react";
import { tryApiGet } from "../api/client";
import { useAppData } from "../state/AppData";
import { navigate } from "../lib/router";
import { colorFor, STATUS } from '../lib/definitions/palette';
import { hazardDecisionInfo } from '../lib/definitions/hazards';
import { clockTime, relativeTime } from "../lib/format";
import type { HazardEventDetail } from "../api/types";

// The single-event drill-down GET /api/hazards/events/{id} adds one thing
// the list view doesn't have: `gating_trace`, every rule the dispatcher
// evaluated for this event, including the ones that PASSED — these rules
// encode hard-won lessons from prior hazard-routing work and are
// the most interesting thing in this phase. Shown in full, not just the
// one rule that decided the outcome, so the whole decision is auditable
// rather than asserted.
//
// `rendered_message` is the point of this page (spec: the operator should be able
// to read what the mesh would have received — do not summarise it) — it
// leads, shown whole in a wrapped, verbatim block, never truncated or
// paraphrased.

function humanizeCode(code: string): string {
  const s = code.replace(/_/g, " ");
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function HazardEventDetailPage({ id }: { id: string }) {
  const { effectiveTheme } = useAppData();
  const [event, setEvent] = useState<HazardEventDetail | null | "unavailable">(null);

  useEffect(() => {
    let cancelled = false;
    setEvent(null);
    tryApiGet<HazardEventDetail>(`/api/hazards/events/${encodeURIComponent(id)}`)
      .then((e) => !cancelled && setEvent(e ?? "unavailable"))
      .catch(() => !cancelled && setEvent("unavailable"));
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (event === null) {
    return (
      <div className="node-detail-page">
        <button className="back-link" onClick={() => navigate("#/hazards?tab=logs")}>
          ← Back to alerts
        </button>
        <div className="node-detail-note">Loading…</div>
      </div>
    );
  }

  if (event === "unavailable") {
    return (
      <div className="node-detail-page">
        <button className="back-link" onClick={() => navigate("#/hazards?tab=logs")}>
          ← Back to alerts
        </button>
        <div className="empty-state">Event {id} not found, or /api/hazards/events/{"{id}"} didn't respond as expected.</div>
      </div>
    );
  }

  const info = hazardDecisionInfo(event.decision);

  return (
    <div className="node-detail-page">
      <button className="back-link" onClick={() => navigate("#/hazards?tab=logs")}>
        ← Back to alerts
      </button>

      <div className="node-detail-header">
        <h2>{event.headline}</h2>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          <span className="badge" style={{ background: colorFor(info.color, effectiveTheme) }}>
            {info.label}
          </span>
          <span className="badge" style={{ background: "var(--ink-muted)" }}>
            {event.adapter_label}
          </span>
          {event.sent && (
            <span className="badge" style={{ background: colorFor(STATUS.critical, effectiveTheme) }}>
              SENT — this should never happen under the phase-6 guard
            </span>
          )}
        </div>
      </div>

      {/* --- The point of this page: the exact transmit text, verbatim --- */}
      <div className="node-detail-section" style={{ marginTop: 0, paddingTop: 0, border: "none" }}>
        <h3>Rendered message (what the mesh would have received)</h3>
        <div className="node-detail-json" style={{ whiteSpace: "pre-wrap", fontSize: 14, lineHeight: 1.5 }}>
          {event.rendered_message}
        </div>
      </div>

      <div className="node-detail-grid">
        <div className="node-detail-row">
          <span className="k">Reason code</span>
          <span>{humanizeCode(event.reason_code)}</span>
        </div>
        <div className="node-detail-row">
          <span className="k">Reason</span>
          <span>{event.reason}</span>
        </div>
        <div className="node-detail-row">
          <span className="k">Severity</span>
          <span>{event.severity || "--"}</span>
        </div>
        <div className="node-detail-row">
          <span className="k">Regions</span>
          <span>{event.regions.length > 0 ? event.regions.join(", ") : "-- (none resolved)"}</span>
        </div>
        <div className="node-detail-row">
          <span className="k">Fetched</span>
          <span>
            {relativeTime(event.fetched_at)} ({clockTime(event.fetched_at)})
          </span>
        </div>
        <div className="node-detail-row">
          <span className="k">Upstream</span>
          <span>{event.upstream_stale ? "Stale at fetch time" : "Fresh at fetch time"}</span>
        </div>
        <div className="node-detail-row node-detail-row-id">
          <span className="k">Would send to</span>
          <span>
            {event.would_send_to.length > 0
              ? event.would_send_to.map((w) => `${w.transport} → ${w.channel}`).join(", ")
              : "-- (no destination resolved)"}
          </span>
        </div>
      </div>

      {/* --- Gating trace: every rule evaluated, passed or not --- */}
      <div className="node-detail-section">
        <h3>Gating trace</h3>
        {event.gating_trace.length === 0 ? (
          <div className="empty-state">No gating trace recorded for this event.</div>
        ) : (
          <div className="node-list-table-wrap">
            <table className="node-list-table">
              <thead>
                <tr>
                  <th>Rule</th>
                  <th>Result</th>
                  <th>Detail</th>
                </tr>
              </thead>
              <tbody>
                {event.gating_trace.map((t, i) => (
                  <tr key={`${t.rule}-${i}`}>
                    <td>{t.rule}</td>
                    <td>
                      {/* "Did not pass" rather than "Failed" — a rule
                          blocking a broadcast (cold-start, no geometry,
                          ...) is very often the system working as
                          designed, not an error; a red "FAILED" badge
                          would misrepresent that the same way an alarming
                          suppressed-events list would. */}
                      <span className="badge" style={{ background: t.passed ? colorFor(STATUS.good, effectiveTheme) : "var(--ink-muted)" }}>
                        {t.passed ? "Passed" : "Did not pass"}
                      </span>
                    </td>
                    <td>{t.detail}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
